import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Config } from "@eagerdog/config";
import { Constants } from "@eagerdog/constants";
import { httpService } from "src/services/http.service";
import { toast } from "src/components/Toast/ToastManager";
import { helperService } from "src/services/helper.service";
import Button from "src/components/Button/Button";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import styles from "./StoreCheckout.module.scss";
import type { ICartItem } from "../KennelClub/components/StoreTab/StoreTab";

const PAYMENT_OPTIONS: IOption[] = [
  { value: "Credit Card", id: Constants.payment_method_type.card },
  { value: "Cash", id: Constants.payment_method_type.cash },
  { value: "Cheque", id: Constants.payment_method_type.cheque },
  { value: "E-Transfer", id: Constants.payment_method_type.etransfer },
  { value: "Other", id: Constants.payment_method_type.other },
];

interface ICustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const CheckoutForm: React.FC<{ clientSecret: string; clubId: string; onSuccess: () => void }> = ({
  clientSecret,
  clubId,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${Config.web.REACT_APP_WEB_URL}/k/${clubId}/store/success`,
        },
        redirect: "if_required",
      });

      if (error) {
        toast.show({
          title: "Payment Failed",
          content: error.message || "Failed to process payment",
          type: "fail",
          duration: 5000,
        });
      } else {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.show({
        title: "Payment Failed",
        content: "An unexpected error occurred",
        type: "fail",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.checkoutForm}>
      <PaymentElement />
      <Button type="submit" disabled={!stripe || loading} className={styles.payButton}>
        {loading ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
};

const StoreCheckout: React.FC = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isManager = (location.state as any)?.isManager || false;
  
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cartItems, setCartItems] = useState<ICartItem[]>([]);
  const [purchaseItems, setPurchaseItems] = useState<{ add_on_id: string; quantity: number }[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  
  // Manager payment options
  const [paymentMethod, setPaymentMethod] = useState<IOption>(PAYMENT_OPTIONS[0]);
  const [chequeNumber, setChequeNumber] = useState<string>("");

  // Customer information
  const [customerInfo, setCustomerInfo] = useState<ICustomerInfo>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const isCardPayment = paymentMethod.id === Constants.payment_method_type.card;

  // Pre-fill customer info on mount for dog owners
  // Removed scroll-to-top: browser handles scroll on navigation naturally
  useEffect(() => {
    
    // Check if user is logged in (skip check for managers as they should always be logged in)
    if (!isManager) {
      const user = helperService.getUser();
      if (!user._id || !helperService.isLoggedIn()) {
        toast.show({
          title: "Login Required",
          content: "You must be logged in to checkout",
          duration: 5000,
          type: "fail"
        });
        
        navigate(`/login?r=/k/${clubId}/store/checkout`);
        return;
      }
      
      // Pre-fill customer info from user account (for dog owners)
      setCustomerInfo({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        email: user.email || "",
        phone: user.phone || "",
      });
    }
  }, [isManager, clubId, navigate]);

  useEffect(() => {
    const initCheckout = async () => {
      // Skip if we haven't loaded customer info yet for dog owners
      if (!isManager && !customerInfo.email) {
        return;
      }

      // Skip if already initialized (check if summary exists)
      if (summary) {
        return;
      }

      try {
        // Load cart from localStorage
        const storedCart = localStorage.getItem(`club_store_cart_${clubId}`);
        if (!storedCart) {
          navigate(`/k/${clubId}`);
          return;
        }

        const cart: ICartItem[] = JSON.parse(storedCart);
        setCartItems(cart);

        const items = cart.map((item) => ({
          add_on_id: item._id,
          quantity: item.quantity,
        }));
        setPurchaseItems(items);

        // Get purchase summary (includes account_number)
        const summaryResponse = await httpService.post(
          `${Config.web.REACT_APP_API_URL}/api/clubs/${clubId}/store/purchase/summary`,
          { items, payment_method_type: "card" }
        );
        setSummary(summaryResponse.data);

        // For dog owners (not managers), create payment intent immediately with their user info
        if (!isManager) {
          const accountNumber = summaryResponse.data.account_number;
          setStripePromise(loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "", {
            stripeAccount: accountNumber,
          }));

          // Create payment intent with logged-in user's info
          const intentResponse = await httpService.post(
            `${Config.web.REACT_APP_API_URL}/api/clubs/${clubId}/store/purchase/intent`,
            {
              items,
              payment_method_type: "card",
              customer_first_name: customerInfo.firstName,
              customer_last_name: customerInfo.lastName,
              customer_email: customerInfo.email,
              customer_phone: customerInfo.phone,
            }
          );

          setClientSecret(intentResponse.data.client_secret);
          setPaymentIntentId(intentResponse.data.payment_intent_id);
        }
      } catch (error: any) {
        console.error("Failed to initialize checkout:", error);
        toast.show({
          title: "Error",
          content: error.response?.data?.message || "Failed to initialize checkout",
          type: "fail",
          duration: 5000,
        });
        navigate(`/k/${clubId}`);
      } finally {
        setLoading(false);
      }
    };

    initCheckout();
  }, [clubId, navigate, isManager, customerInfo, summary]);

  // Load Stripe when manager selects card payment (but don't create intent yet)
  useEffect(() => {
    const loadStripeForManager = async () => {
      if (isManager && isCardPayment && summary && !stripePromise) {
        const accountNumber = summary.account_number;
        setStripePromise(loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "", {
          stripeAccount: accountNumber,
        }));
        // Intent will be created when manager clicks "Proceed to Payment"
      }
    };

    loadStripeForManager();
  }, [isManager, isCardPayment, summary, stripePromise, clubId, purchaseItems]);

  const validateCustomerInfo = (): boolean => {
    if (!customerInfo.firstName.trim()) {
      toast.show({ title: "Error", content: "First name is required", type: "fail", duration: 5000 });
      return false;
    }
    if (!customerInfo.lastName.trim()) {
      toast.show({ title: "Error", content: "Last name is required", type: "fail", duration: 5000 });
      return false;
    }
    if (!customerInfo.email.trim()) {
      toast.show({ title: "Error", content: "Email is required", type: "fail", duration: 5000 });
      return false;
    }
    return true;
  };

  // Create payment intent with customer details (for non-manager card payments)
  const createPaymentIntentWithCustomerInfo = async () => {
    if (!validateCustomerInfo()) {
      throw new Error("Invalid customer information");
    }

    const intentResponse = await httpService.post(
      `${Config.web.REACT_APP_API_URL}/api/clubs/${clubId}/store/purchase/intent`,
      {
        items: purchaseItems,
        payment_method_type: "card",
        customer_first_name: customerInfo.firstName,
        customer_last_name: customerInfo.lastName,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
      }
    );

    setClientSecret(intentResponse.data.client_secret);
    setPaymentIntentId(intentResponse.data.payment_intent_id);
    return intentResponse.data.client_secret;
  };

  const handleSuccess = async () => {
    if (!paymentIntentId) return;
    if (!validateCustomerInfo()) return;

    try {
      // Complete the purchase on the backend
      await httpService.post(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${clubId}/store/purchase`,
        {
          items: purchaseItems,
          payment_method_type: "card",
          payment_intent_id: paymentIntentId,
          customer_first_name: customerInfo.firstName,
          customer_last_name: customerInfo.lastName,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
        }
      );

      // Clear cart
      localStorage.removeItem(`club_store_cart_${clubId}`);
      
      toast.show({
        title: "Success!",
        content: "Your purchase was successful!",
        type: "success",
        duration: 5000,
      });

      // Redirect managers to store transactions, dog owners to their transactions tab
      if (isManager) {
        navigate(`/k/${clubId}/manage/store-transactions`);
      } else {
        navigate(`/account?t=4`);
      }
    } catch (error: any) {
      console.error("Failed to complete purchase:", error);
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Payment succeeded but failed to record purchase. Please contact support.",
        type: "fail",
        duration: 10000,
      });
    }
  };

  // Handle non-card payment for managers
  const handleOfflinePayment = async () => {
    if (!validateCustomerInfo()) return;
    
    if (paymentMethod.id === Constants.payment_method_type.cheque && !chequeNumber.trim()) {
      toast.show({
        title: "Error",
        content: "Please enter a cheque number",
        type: "fail",
        duration: 5000,
      });
      return;
    }

    setSubmitting(true);

    try {
      await httpService.post(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${clubId}/store/purchase`,
        {
          items: purchaseItems,
          payment_method_type: paymentMethod.id,
          cheque_number: paymentMethod.id === Constants.payment_method_type.cheque ? chequeNumber : undefined,
          customer_first_name: customerInfo.firstName,
          customer_last_name: customerInfo.lastName,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
        }
      );

      // Clear cart
      localStorage.removeItem(`club_store_cart_${clubId}`);
      
      toast.show({
        title: "Success!",
        content: "Purchase recorded successfully!",
        type: "success",
        duration: 5000,
      });

      // Redirect to store transactions tab
      navigate(`/k/${clubId}/manage/store-transactions`);
    } catch (error: any) {
      console.error("Failed to record purchase:", error);
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to record purchase",
        type: "fail",
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <span className="fa fa-spinner fa-spin" />
        <p>Loading checkout...</p>
      </div>
    );
  }

  // For non-managers, require Stripe to be ready
  if (!isManager && (!clientSecret || !summary)) {
    return null;
  }

  // For managers with offline payment, we just need summary
  if (isManager && !summary) {
    return null;
  }

  const currency = summary.amounts.currency || "CAD";
  const multiplier = summary.amounts.amount_multiplier;
  
  // Customers now pay only the item price (fees deducted from club)
  // So we show the calculated_total_amount which equals the item price
  const subtotal = summary.amounts.calculated_total_amount * multiplier;
  const totalAmount = subtotal;

  return (
    <div className={styles.storeCheckout}>
      <div className={styles.checkoutContainer}>
        {/* Left side: Customer Info + Payment */}
        <div className={styles.leftColumn}>
          {/* Customer Information Section */}
          <div className={styles.customerSection}>
            <h2>Customer Information</h2>
            <div className={styles.formRow}>
              <Input
                label="First Name"
                type="text"
                value={customerInfo.firstName}
                onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                placeholder="First name"
                required
                disabled={!isManager}
              />
              <Input
                label="Last Name"
                type="text"
                value={customerInfo.lastName}
                onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                placeholder="Last name"
                required
                disabled={!isManager}
              />
            </div>
            <div className={styles.formRow}>
              <Input
                label="Email"
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                placeholder="Email address"
                required
                disabled={!isManager}
              />
              <Input
                label="Phone"
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                placeholder="Phone number (optional)"
                disabled={!isManager}
              />
            </div>
          </div>

          {/* Proceed to Payment button for managers with card payment */}
          {isManager && isCardPayment && !clientSecret && (
            <div className={styles.proceedButton}>
              <Button
                onClick={async () => {
                  try {
                    setSubmitting(true);
                    await createPaymentIntentWithCustomerInfo();
                  } catch (error) {
                    console.error("Failed to create payment intent:", error);
                    toast.show({
                      title: "Error",
                      content: "Failed to initialize payment. Please check customer information.",
                      type: "fail",
                      duration: 5000,
                    });
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                className={styles.payButton}
                style={{ minWidth: '200px', whiteSpace: 'nowrap' }}
              >
                {submitting ? "Processing..." : "Proceed to Payment"}
              </Button>
            </div>
          )}

          {/* Payment Section - only show after payment intent is created for manager card payments */}
          {(!isManager || !isCardPayment || clientSecret) && (
          <div className={styles.paymentSection}>
            <h2>Payment Details</h2>
            
            {/* Manager payment type selection */}
            {isManager && (
              <div className={styles.paymentTypeSelector}>
                <Dropdown
                  label="Payment Type"
                  placeholder="Select payment type"
                  options={PAYMENT_OPTIONS}
                  value={paymentMethod}
                  onChange={(e, option) => {
                    setPaymentMethod(option);
                    // Reset Stripe if switching away from card
                    if (option.id !== Constants.payment_method_type.card) {
                      setStripePromise(null);
                      setClientSecret(null);
                      setPaymentIntentId(null);
                    }
                  }}
                />
                
                {/* Show cheque number input if cheque is selected */}
                {paymentMethod.id === Constants.payment_method_type.cheque && (
                  <div className={styles.chequeInput}>
                    <Input
                      label="Cheque Number"
                      type="text"
                      value={chequeNumber}
                      onChange={(e) => setChequeNumber(e.target.value)}
                      placeholder="Enter cheque number"
                      required
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Show Stripe for card payments */}
            {((!isManager || isCardPayment) && stripePromise && clientSecret) ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: { theme: "stripe" },
                }}
              >
                <CheckoutForm clientSecret={clientSecret} clubId={clubId!} onSuccess={handleSuccess} />
              </Elements>
            ) : (
              /* Show confirm button for non-card manager payments */
              isManager && !isCardPayment && (
                <div className={styles.offlinePayment}>
                  <p className={styles.offlineNote}>
                    This purchase will be recorded without processing an online payment.
                  </p>
                  <Button 
                    onClick={handleOfflinePayment} 
                    disabled={submitting}
                    className={styles.payButton}
                  >
                    {submitting ? "Processing..." : "Confirm Purchase"}
                  </Button>
                </div>
              )
            )}
          </div>
          )}
        </div>

        {/* Right side: Order Summary */}
        <div className={styles.orderSummary}>
          <h2>Order Summary</h2>
          <div className={styles.items}>
            {cartItems.map((item) => (
              <div key={item._id} className={styles.item}>
                <div>
                  <h4>{item.title}</h4>
                  <p>Quantity: {item.quantity}</p>
                </div>
                <span>${(item.fee * item.quantity).toFixed(2)} {currency}</span>
              </div>
            ))}
          </div>
          <div className={styles.totals}>
            <div className={styles.row}>
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)} {currency}</span>
            </div>
            <div className={styles.totalRow}>
              <strong>Total:</strong>
              <strong>${totalAmount.toFixed(2)} {currency}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreCheckout;

